import React, { useState, useEffect, useMemo } from 'react';
import Grid from '../components/Grid';
import { API_BASE_URL } from '../constants/property';

const toImageSrc = (rawImageData) => {
  if (!rawImageData) return null;
  const v = String(rawImageData).trim();
  if (!v) return null;
  if (v.startsWith('data:image/')) return v;
  if (/^https?:\/\//i.test(v)) return v;
  return `data:image/png;base64,${v}`;
};

/** Calendar key YYYY-MM-DD for grouping / date filter */
const toAttendanceDateKey = (dateVal) => {
  if (dateVal == null || dateVal === '') return '';
  if (typeof dateVal === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateVal.trim())) {
    return dateVal.trim().slice(0, 10);
  }
  const d = new Date(dateVal);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatAttendanceDateDisplay = (dateVal) => {
  if (dateVal == null || dateVal === '') return '—';
  const key = toAttendanceDateKey(dateVal);
  if (!key) return String(dateVal);
  const [yy, mm, dd] = key.split('-').map(Number);
  const d = new Date(yy, mm - 1, dd);
  if (Number.isNaN(d.getTime())) return String(dateVal);
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

const attendanceDateSortTime = (dateVal) => {
  const t = new Date(dateVal || 0).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const parseDateValue = (dateValue) => {
  if (!dateValue && dateValue !== 0) return new Date(0);
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue.trim())) {
    const [year, month, day] = dateValue.trim().split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(dateValue);
  return Number.isNaN(parsed.getTime())
    ? new Date(0)
    : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const isDateInRange = (date, startDate, endDate) => {
  if (!startDate || !endDate || !date) return false;
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const s = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const e = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  return d >= s && d <= e;
};

const normalizeAttendanceGalleryItems = (data) => {
  if (!Array.isArray(data)) return [];
  return data.map((item, index) => {
    const empId =
      item.empId != null && item.empId !== ''
        ? String(item.empId).trim()
        : item.employeeId != null && item.employeeId !== ''
          ? String(item.employeeId).trim()
          : item.empID != null && item.empID !== ''
            ? String(item.empID).trim()
            : '';
    const firstName = String(item.firstName ?? '').trim();
    const lastName = String(item.lastName ?? '').trim();
    const location =
      item.location != null && item.location !== '' ? String(item.location).trim() : '';
    const imageSrc = toImageSrc(
      item.imageData ?? item.employeeImage ?? item.photoBase64 ?? item.photo ?? null
    );
    const attendanceDate = item.attendanceDate ?? null;
    return {
      id: item.id ?? item.attendanceId ?? `g-${index}`,
      empId,
      firstName,
      lastName,
      location,
      imageSrc,
      attendanceDate,
      attendanceDateKey: toAttendanceDateKey(attendanceDate),
      raw: item,
    };
  });
};

const Gallery = () => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFormData, setUploadFormData] = useState({
    title: '',
    category: '',
    description: '',
    imageFile: null,
    imagePreview: null,
  });

  const [galleryItems, setGalleryItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const [filterEmpId, setFilterEmpId] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterFirstName, setFilterFirstName] = useState('');
  const [filterLastName, setFilterLastName] = useState('');

  const [datePreset, setDatePreset] = useState('');
  const [dateDisplay, setDateDisplay] = useState('');
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showCustomCalendar, setShowCustomCalendar] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setFetchError('');
      try {
        const response = await fetch(`${API_BASE_URL}/api/attendance`, {
          method: 'GET',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        if (!cancelled) {
          setGalleryItems(normalizeAttendanceGalleryItems(data));
        }
      } catch (err) {
        console.error('Gallery: failed to load attendance', err);
        if (!cancelled) {
          setFetchError(err.message || 'Failed to load gallery');
          setGalleryItems([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!datePreset) {
      setDateDisplay('');
      return;
    }

    const now = new Date();
    const toDisplay = (d) =>
      d.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      });
    const clone = (d) => new Date(d.getTime());
    let start = clone(now);
    let end = clone(now);

    switch (datePreset) {
      case 'today':
        break;
      case 'yesterday':
        start.setDate(start.getDate() - 1);
        end.setDate(end.getDate() - 1);
        break;
      case 'last7':
        start.setDate(start.getDate() - 6);
        break;
      case 'last30':
        start.setDate(start.getDate() - 29);
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'lastMonth': {
        const firstLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        start = firstLastMonth;
        end = lastLastMonth;
        break;
      }
      case 'custom':
        if (!dateDisplay) {
          setDateDisplay(`${toDisplay(start)} - ${toDisplay(end)}`);
        }
        return;
      default:
        break;
    }
    setDateDisplay(`${toDisplay(start)} - ${toDisplay(end)}`);
  }, [datePreset]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDateDropdown && !event.target.closest('.date-dropdown-container')) {
        setShowDateDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDateDropdown]);

  const filteredItems = useMemo(() => {
    let startDate = null;
    let endDate = null;
    const today = new Date();

    if (datePreset && datePreset !== 'custom') {
      switch (datePreset) {
        case 'today': {
          const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          startDate = d;
          endDate = d;
          break;
        }
        case 'yesterday': {
          const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
          startDate = d;
          endDate = d;
          break;
        }
        case 'last7': {
          endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);
          break;
        }
        case 'last30': {
          endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29);
          break;
        }
        case 'thisMonth': {
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          break;
        }
        case 'lastMonth': {
          startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          endDate = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
        }
        default:
          break;
      }
    } else if (datePreset === 'custom' && customStartDate && customEndDate) {
      startDate = customStartDate;
      endDate = customEndDate;
    }

    const eid = filterEmpId.trim().toLowerCase();
    const loc = filterLocation.trim().toLowerCase();
    const fn = filterFirstName.trim().toLowerCase();
    const ln = filterLastName.trim().toLowerCase();

    return galleryItems.filter((item) => {
      if (eid && !item.empId.toLowerCase().includes(eid)) return false;
      if (loc && !item.location.toLowerCase().includes(loc)) return false;
      if (fn && !item.firstName.toLowerCase().includes(fn)) return false;
      if (ln && !item.lastName.toLowerCase().includes(ln)) return false;

      let dateOk = true;
      if (startDate && endDate) {
        const key = item.attendanceDateKey;
        const raw = item.attendanceDate;
        if (!key && (raw == null || raw === '')) {
          dateOk = false;
        } else {
          const itemDate = parseDateValue(key || raw);
          dateOk = isDateInRange(itemDate, startDate, endDate);
        }
      }
      return dateOk;
    });
  }, [
    galleryItems,
    filterEmpId,
    filterLocation,
    filterFirstName,
    filterLastName,
    datePreset,
    customStartDate,
    customEndDate,
  ]);

  /** Newest calendar day first; items without date last. */
  const groupedItemsByDate = useMemo(() => {
    const sorted = [...filteredItems].sort(
      (a, b) => attendanceDateSortTime(b.attendanceDate) - attendanceDateSortTime(a.attendanceDate)
    );
    const map = new Map();
    for (const item of sorted) {
      const key = item.attendanceDateKey || '_nodate';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    }
    return [...map.entries()].sort(([ka], [kb]) => {
      if (ka === '_nodate') return 1;
      if (kb === '_nodate') return -1;
      return kb.localeCompare(ka);
    });
  }, [filteredItems]);

  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    setShowDateDropdown(false);
    if (preset === 'custom') {
      setShowCustomCalendar(true);
    } else {
      setShowCustomCalendar(false);
    }
  };

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const formatCal = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const isSameDate = (date1, date2) => {
    if (!date1 || !date2) return false;
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const handleDateClick = (date) => {
    if (!customStartDate || (customStartDate && customEndDate)) {
      setCustomStartDate(date);
      setCustomEndDate(null);
    } else if (customStartDate && !customEndDate) {
      if (date < customStartDate) {
        setCustomEndDate(customStartDate);
        setCustomStartDate(date);
      } else {
        setCustomEndDate(date);
      }
    }
  };

  const handleApplyCustomRange = () => {
    if (customStartDate && customEndDate) {
      setDateDisplay(`${formatCal(customStartDate)} - ${formatCal(customEndDate)}`);
      setShowCustomCalendar(false);
      setShowDateDropdown(false);
    }
  };

  const handleCancelCustomRange = () => {
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
    setCustomStartDate(null);
    setCustomEndDate(null);
  };

  const handleClearDate = (e) => {
    e.stopPropagation();
    setDatePreset('');
    setDateDisplay('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const handleUploadClick = () => {
    setIsUploadModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsUploadModalOpen(false);
    setUploadFormData({
      title: '',
      category: '',
      description: '',
      imageFile: null,
      imagePreview: null,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUploadFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFormData((prev) => ({
        ...prev,
        imageFile: file,
        imagePreview: URL.createObjectURL(file),
      }));
    }
  };

  const handleUpload = () => {
    console.log('Uploading image:', uploadFormData);
    handleCloseModal();
  };

  const clearFilters = () => {
    setFilterEmpId('');
    setFilterLocation('');
    setFilterFirstName('');
    setFilterLastName('');
    setDatePreset('');
    setDateDisplay('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
  };

  const hasActiveFilters =
    datePreset !== '' ||
    dateDisplay !== '' ||
    filterEmpId.trim() ||
    filterLocation.trim() ||
    filterFirstName.trim() ||
    filterLastName.trim();

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-poppins">Gallery</h1>
          <p className="text-sm text-gray-500 mt-1">
            Photos from attendance records ({filteredItems.length}
            {galleryItems.length !== filteredItems.length ? ` of ${galleryItems.length}` : ''})
          </p>
        </div>
        {/* <button type="button" onClick={handleUploadClick} className="btn-primary">
          Upload Image
        </button> */}
      </div>

      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-wrap items-end gap-x-3 gap-y-3">
          <div className="w-full max-w-[300px] shrink-0">
            <label className="text-xs text-gray-600 mb-1 block">
              {datePreset === '' ? 'Choose Date' : 'Date Range:'}
              {datePreset !== '' && (
                <span className="font-bold text-black text-xs uppercase ml-1">
                  (
                  {datePreset === 'today'
                    ? 'Today'
                    : datePreset === 'yesterday'
                      ? 'Yesterday'
                      : datePreset === 'last7'
                        ? 'Last 7 Days'
                        : datePreset === 'last30'
                          ? 'Last 30 Days'
                          : datePreset === 'thisMonth'
                            ? 'This Month'
                            : datePreset === 'lastMonth'
                              ? 'Last Month'
                              : datePreset === 'custom'
                                ? 'Custom Range'
                                : datePreset}
                  )
                </span>
              )}
            </label>
            <div className="relative date-dropdown-container">
              <div className="relative">
                <input
                  type="text"
                  value={dateDisplay || 'Select range'}
                  readOnly
                  title={dateDisplay || undefined}
                  onClick={() => setShowDateDropdown(!showDateDropdown)}
                  className="w-full px-3 py-2 pl-9 pr-8 border border-gray-300 rounded-md text-sm bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent truncate"
                  placeholder="Select date range"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                {dateDisplay && (
                  <button
                    type="button"
                    onClick={handleClearDate}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-100 rounded-r-md"
                    title="Clear date"
                  >
                    <svg
                      className="h-4 w-4 text-gray-400 hover:text-gray-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
              {showDateDropdown && (
                <div className="absolute top-full left-0 mt-1 w-full min-w-[240px] bg-white border border-gray-300 rounded-md shadow-lg z-20">
                  <div className="py-1">
                    {[
                      ['today', 'Today'],
                      ['yesterday', 'Yesterday'],
                      ['last7', 'Last 7 Days'],
                      ['last30', 'Last 30 Days'],
                      ['thisMonth', 'This Month'],
                      ['lastMonth', 'Last Month'],
                      ['custom', 'Custom Range'],
                    ].map(([preset, label]) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => handleDatePresetChange(preset)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${
                          datePreset === preset ? 'bg-orange-100 text-orange-600' : 'text-gray-700'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {showCustomCalendar && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-30 p-4">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="w-48 pr-0 sm:pr-4 sm:border-r border-gray-200">
                      <div className="space-y-1">
                        {[
                          ['today', 'Today'],
                          ['yesterday', 'Yesterday'],
                          ['last7', 'Last 7 Days'],
                          ['last30', 'Last 30 Days'],
                          ['thisMonth', 'This Month'],
                          ['lastMonth', 'Last Month'],
                          ['custom', 'Custom Range'],
                        ].map(([preset, label]) => (
                          <button
                            key={`cal-${preset}`}
                            type="button"
                            onClick={() => handleDatePresetChange(preset)}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${
                              datePreset === preset
                                ? 'bg-orange-100 text-orange-600'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <button
                          type="button"
                          onClick={() => navigateMonth(-1)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <div className="text-sm font-medium text-gray-900">
                          {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </div>
                        <button
                          type="button"
                          onClick={() => navigateMonth(1)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                      <div className="grid grid-cols-7 gap-1 mb-2">
                        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                          <div key={d} className="text-xs text-gray-500 text-center py-1">
                            {d}
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-1">
                        {(() => {
                          const daysInMonth = getDaysInMonth(currentMonth);
                          const firstDay = getFirstDayOfMonth(currentMonth);
                          const days = [];
                          const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 0);
                          for (let i = firstDay - 1; i >= 0; i--) {
                            const day = prevMonth.getDate() - i;
                            days.push(
                              <button
                                key={`prev-${day}`}
                                type="button"
                                className="text-xs text-gray-400 py-1 rounded"
                                disabled
                              >
                                {day}
                              </button>
                            );
                          }
                          for (let day = 1; day <= daysInMonth; day++) {
                            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                            const isSelected =
                              isSameDate(date, customStartDate) || isSameDate(date, customEndDate);
                            const isInRange =
                              customStartDate &&
                              customEndDate &&
                              isDateInRange(date, customStartDate, customEndDate);
                            days.push(
                              <button
                                key={day}
                                type="button"
                                onClick={() => handleDateClick(date)}
                                className={`text-xs py-1 rounded hover:bg-gray-100 ${
                                  isSelected
                                    ? 'bg-orange-500 text-white'
                                    : isInRange
                                      ? 'bg-orange-100 text-orange-600'
                                      : 'text-gray-700'
                                }`}
                              >
                                {day}
                              </button>
                            );
                          }
                          const remainingCells = 42 - days.length;
                          for (let day = 1; day <= remainingCells; day++) {
                            days.push(
                              <button
                                key={`next-${day}`}
                                type="button"
                                className="text-xs text-gray-400 py-1 rounded"
                                disabled
                              >
                                {day}
                              </button>
                            );
                          }
                          return days;
                        })()}
                      </div>
                      <div className="mt-4 text-center">
                        <div className="text-sm text-gray-600 mb-3">
                          {customStartDate && customEndDate
                            ? `${formatCal(customStartDate)} - ${formatCal(customEndDate)}`
                            : customStartDate
                              ? `${formatCal(customStartDate)} - Select end date`
                              : 'Select start date'}
                        </div>
                        <div className="flex gap-2 justify-center">
                          <button
                            type="button"
                            onClick={handleCancelCustomRange}
                            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleApplyCustomRange}
                            disabled={!customStartDate || !customEndDate}
                            className="px-4 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="w-full max-w-[150px] shrink-0">
            <label className="block text-xs font-medium text-gray-600 mb-1">Emp ID</label>
            <input
              type="text"
              value={filterEmpId}
              onChange={(e) => setFilterEmpId(e.target.value)}
              placeholder=""
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="w-full max-w-[300px] shrink-0">
            <label className="block text-xs font-medium text-gray-600 mb-1">Location</label>
            <input
              type="text"
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              placeholder=""
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="w-full max-w-[150px] shrink-0">
            <label className="block text-xs font-medium text-gray-600 mb-1">First Name</label>
            <input
              type="text"
              value={filterFirstName}
              onChange={(e) => setFilterFirstName(e.target.value)}
              placeholder=""
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="w-full max-w-[150px] shrink-0">
            <label className="block text-xs font-medium text-gray-600 mb-1">Last Name</label>
            <input
              type="text"
              value={filterLastName}
              onChange={(e) => setFilterLastName(e.target.value)}
              placeholder=""
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Clear filters
          </button>
        </div>
        </div>
        {/*
          Keep clear control visible when filters are active so layout stays consistent with other pages.
        */}
       
      </div>

      {fetchError && (
        <div className="rounded-md bg-red-50 text-red-800 px-4 py-3 text-sm">{fetchError}</div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
            <p className="text-sm text-gray-600">Loading gallery…</p>
          </div>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-16 text-gray-500 text-sm">
          {galleryItems.length === 0
            ? 'No attendance records found.'
            : 'No records match the current filters.'}
        </div>
      ) : (
        <div className="space-y-10">
          {groupedItemsByDate.map(([dateKey, items]) => (
            <section key={dateKey} className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-800 font-poppins border-b border-gray-200 pb-2">
                {dateKey === '_nodate'
                  ? 'No attendance date'
                  : formatAttendanceDateDisplay(dateKey)}
              </h2>
              <Grid cols={3} gap={6}>
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white shadow rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="aspect-w-16 aspect-h-9 bg-gray-200">
                      <div className="w-full h-48 bg-gray-200 flex items-center justify-center overflow-hidden">
                        {item.imageSrc ? (
                          <img
                            src={item.imageSrc}
                            alt={
                              `${item.firstName} ${item.lastName}`.trim() || item.empId || 'Attendance'
                            }
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <svg
                            className="w-16 h-16 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        )}
                      </div>
                    </div>
                    <div className="p-4 space-y-1">
                      <p className="text-xs text-teal-700 font-medium">
                        {formatAttendanceDateDisplay(item.attendanceDate)}
                      </p>
                      <p className="text-sm font-medium text-gray-900 font-poppins">
                        {[item.firstName, item.lastName].filter(Boolean).join(' ') || '—'}
                      </p>
                      <p className="text-xs text-gray-600">
                        <span className="font-medium text-gray-700">Emp ID:</span> {item.empId || '—'}
                      </p>
                      <p className="text-xs text-gray-600">
                        <span className="font-medium text-gray-700">Location:</span>{' '}
                        {item.location || '—'}
                      </p>
                    </div>
                  </div>
                ))}
              </Grid>
            </section>
          ))}
        </div>
      )}

      {isUploadModalOpen && (
        <div className="fixed inset-0 z-50" style={{ marginTop: 0 }}>
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleCloseModal}
              role="presentation"
            />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Upload New Image
                    </h3>

                    <form className="space-y-4">
                      <div className="flex justify-center">
                        <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                          {uploadFormData.imagePreview ? (
                            <img
                              src={uploadFormData.imagePreview}
                              alt="Preview"
                              className="w-full h-full object-cover rounded-lg"
                            />
                          ) : (
                            <svg
                              className="w-8 h-8 text-gray-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Image *
                        </label>
                        <div className="flex items-center space-x-4">
                          <button
                            type="button"
                            onClick={() => document.getElementById('galleryImage').click()}
                            className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            Choose File
                          </button>
                          <span className="text-sm text-gray-500">
                            {uploadFormData.imageFile ? uploadFormData.imageFile.name : 'No file chosen'}
                          </span>
                        </div>
                        <input
                          type="file"
                          accept="image/jpeg,image/png"
                          onChange={handleImageChange}
                          className="hidden"
                          id="galleryImage"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Image Title *
                        </label>
                        <input
                          type="text"
                          name="title"
                          value={uploadFormData.title}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter image title"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Category *
                        </label>
                        <select
                          name="category"
                          value={uploadFormData.category}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select category</option>
                          <option value="Sitewise">Sitewise</option>
                          <option value="Datewise">Datewise</option>
                          <option value="Geo location">Geo location</option>
                          <option value="Events">Events</option>
                          <option value="Projects">Projects</option>
                          <option value="Team">Team</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description
                        </label>
                        <textarea
                          name="description"
                          value={uploadFormData.description}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter image description (optional)"
                        />
                      </div>
                    </form>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={!uploadFormData.title || !uploadFormData.category || !uploadFormData.imageFile}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Upload Image
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
